import { supabase } from '../lib/supabase';

const SAFE_PROFILE_COLUMNS = [
  'id', 'email', 'full_name', 'avatar_url', 'role', 'bio', 'company',
  'location', 'website', 'is_active', 'created_at', 'updated_at', 'plan_tier',
  'trial_started_at', 'trial_ends_at', 'active_language_profile_id'
].join(',');

/**
 * MAXIMUM DEBUG VERSION - Logs everything at every step
 */

function isValidUUID(str) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Authentication service methods
const signUp = async (email, password, fullName) => {
  try {
    // Sign up the user
    const { data: authData, error: authError } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName
        }
      }
    });

    if (authError) {
      return { data: null, error: authError };
    }

    // Create user profile if user was created
    if (authData?.user) {
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.insert({
          id: authData.user.id,
          full_name: fullName,
          email: email,
          role: 'user'
        })
        ?.select(SAFE_PROFILE_COLUMNS)
        ?.single();

      if (profileError) {
        console.error('Error creating profile:', profileError);
        // Don't fail the signup if profile creation fails
        // The profile might be created by a database trigger
      }

      return { data: { user: authData.user, profile: profileData }, error: null };
    }

    return { data: authData, error: null };
  } catch (error) {
    return { data: null, error: { message: error?.message || 'An unexpected error occurred' } };
  }
};

const updateProfile = async (userId, updates) => {
  try {
    // Map the updates to match the database schema
    const profileUpdates = {
      full_name: updates?.fullName || updates?.full_name,
      avatar_url: updates?.avatarUrl || updates?.avatar_url,
      bio: updates?.bio,
      company: updates?.company,
      location: updates?.location,
      website: updates?.website
    };

    // Remove undefined values
    Object.keys(profileUpdates).forEach(key => {
      if (profileUpdates[key] === undefined) {
        delete profileUpdates[key];
      }
    });

    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update(profileUpdates)
      ?.eq('id', userId)
      ?.select(SAFE_PROFILE_COLUMNS)
      ?.single();

    return { data, error };
  } catch (error) {
    return { data: null, error: { message: error?.message || 'An unexpected error occurred' } };
  }
};

// Export authService object
export const authService = {
  signUp,
  updateProfile
};

export async function fetchArticlesFromWP(siteUrl) {
  console.log('═══════════════════════════════════════');
  console.log('🌐 WORDPRESS API FETCH START');
  console.log('═══════════════════════════════════════');
  console.log('URL:', siteUrl);
  
  try {
    const res = await fetch(`${siteUrl}/wp-json/wp/v2/posts?_embed&per_page=50`);
    console.log('Response status:', res.status);
    console.log('Response ok?', res.ok);

    if (!res.ok) {
      throw new Error(`WordPress API error: ${res.status}`);
    }

    const data = await res?.json();
    console.log('✅ WordPress returned', data?.length, 'posts');

    const mappedArticles = data?.map((post) => {
      const featuredImage = post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || post?.external_featured_image || null;
      let secondImage = null;
      
      if (post?.content?.rendered) {
        const imgMatches = post.content.rendered.match(/<img[^>]+src=["']([^"']+)["']/gi);
        if (imgMatches && imgMatches.length > 0) {
          for (const match of imgMatches) {
            const src = match.match(/src=["']([^"']+)["']/i)?.[1];
            if (src && src !== featuredImage) {
              secondImage = src;
              break;
            }
          }
        }
      }
      
      return {
        id: post?.id,
        title: post?.title?.rendered,
        date: post?.date,
        image: featuredImage,
        secondImage: secondImage,
        url: post?.link || null,
        source: 'wordpress'
      };
    }) || [];
    
    console.log('✅ Mapped to', mappedArticles.length, 'articles');
    console.log('═══════════════════════════════════════');
    return mappedArticles;
  } catch (error) {
    console.error('❌ WordPress API Error:', error);
    console.log('═══════════════════════════════════════');
    return [];
  }
}

export async function fetchArticlesFromDB(siteId) {
  console.log('═══════════════════════════════════════');
  console.log('🗄️  DATABASE FETCH START');
  console.log('═══════════════════════════════════════');
  console.log('📍 Step 1: Getting authenticated user...');
  
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    
    if (userError) {
      console.error('❌ Error getting user:', userError);
      return [];
    }
    
    if (!user) {
      console.log('⚠️ No user authenticated');
      console.log('═══════════════════════════════════════');
      return [];
    }

    console.log('✅ User authenticated');
    console.log('   User ID:', user?.id);
    console.log('   Email:', user?.email);
    
    console.log('');
    console.log('📍 Step 2: Analyzing site ID...');
    console.log('   Site ID provided:', siteId);
    console.log('   Site ID type:', typeof siteId);
    console.log('   Is valid UUID?', isValidUUID(siteId));
    
    console.log('');
    console.log('📍 Step 3: Fetching ALL articles for this user (no filters)...');
    
    const { data: allArticles, error: allError } = await supabase
      ?.from('wp_articles')
      ?.select('*')
      ?.eq('user_id', user?.id);

    if (allError) {
      console.error('❌ Error querying all articles:', allError);
      console.log('   Error code:', allError.code);
      console.log('   Error message:', allError.message);
      console.log('   Error details:', allError.details);
      console.log('═══════════════════════════════════════');
      return [];
    }

    console.log('✅ Query successful!');
    console.log('   Total articles found:', allArticles?.length || 0);
    
    if (!allArticles || allArticles.length === 0) {
      console.log('');
      console.log('⚠️ NO ARTICLES IN DATABASE FOR THIS USER');
      console.log('   This means the wp_articles table is empty for user:', user?.id);
      console.log('═══════════════════════════════════════');
      return [];
    }
    
    console.log('');
    console.log('📍 Step 4: Analyzing article statuses...');
    const statusCounts = {};
    const siteIdCounts = {};
    
    allArticles.forEach(article => {
      const status = article?.status || 'null';
      const articleSiteId = article?.site_id || 'null';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      siteIdCounts[articleSiteId] = (siteIdCounts[articleSiteId] || 0) + 1;
    });
    
    console.log('   Articles by status:', statusCounts);
    console.log('   Articles by site_id:', siteIdCounts);
    
    console.log('');
    console.log('📍 Step 5: Sample articles (first 3):');
    allArticles.slice(0, 3).forEach((article, idx) => {
      console.log(`   Article ${idx + 1}:`, {
        id: article?.id,
        title: article?.title || article?.focus_keyword || 'NO TITLE',
        status: article?.status || 'NO STATUS',
        site_id: article?.site_id || 'NO SITE_ID',
        has_main_image: !!article?.image_main_url,
        created_at: article?.created_at
      });
    });
    
    console.log('');
    console.log('📍 Step 6: Building filtered query...');
    
    let query = supabase
      ?.from('wp_articles')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false })
      ?.limit(50);

    console.log('   ✓ Filter by user_id:', user?.id);
    console.log('   ✓ Order by created_at DESC');
    console.log('   ✓ Limit 50');

    if (siteId && isValidUUID(siteId)) {
      console.log('   ✓ Filter by site_id:', siteId);
      query = query?.eq('site_id', siteId);
    } else {
      console.log('   ✗ NO site_id filter (will get all articles for user)');
    }

    console.log('');
    console.log('📍 Step 7: Executing filtered query...');
    const { data: articles, error } = await query;

    if (error) {
      console.error('❌ Error executing filtered query:', error);
      console.log('═══════════════════════════════════════');
      return [];
    }

    console.log('✅ Filtered query successful!');
    console.log('   Articles returned:', articles?.length || 0);

    if (!articles || articles.length === 0) {
      console.log('');
      console.log('⚠️ NO ARTICLES MATCH THE FILTER');
      if (siteId && isValidUUID(siteId)) {
        console.log('   Possible reasons:');
        console.log('   - No articles have site_id =', siteId);
        console.log('   - Check siteIdCounts above to see what site_ids exist');
      }
      console.log('═══════════════════════════════════════');
      return [];
    }

    console.log('');
    console.log('📍 Step 8: Mapping articles for UI...');
    const mappedArticles = articles?.map((article) => {
      const featuredImage = article?.image_main_url || null;
      // Check both singular and plural column names from database
      const focusKeywordValue = article?.focus_keywords || article?.focus_keyword || article?.focusKeyword || '';
      
      return {
        id: article?.id,
        title: article?.title || focusKeywordValue || 'Untitled',
        focusKeyword: focusKeywordValue,
        focus_keyword: focusKeywordValue,
        focus_keywords: focusKeywordValue, // Plural version for consistency
        date: article?.created_at || article?.published_at || new Date().toISOString(),
        image: featuredImage,
        secondImage: article?.image_top_url || null,
        url: article?.wp_post_url || article?.wordpress_url || null,
        source: 'database',
        status: article?.status
      };
    }) || [];

    console.log('✅ Mapped', mappedArticles.length, 'articles');
    console.log('');
    console.log('📍 Step 9: Sample mapped articles (first 2):');
    mappedArticles.slice(0, 2).forEach((article, idx) => {
      console.log(`   Mapped Article ${idx + 1}:`, {
        title: article.title,
        status: article.status,
        has_image: !!article.image,
        source: article.source
      });
    });
    
    console.log('');
    console.log('✅ DATABASE FETCH COMPLETE');
    console.log('   Returning', mappedArticles.length, 'articles');
    console.log('═══════════════════════════════════════');
    return mappedArticles;
    
  } catch (error) {
    console.error('');
    console.error('💥 UNEXPECTED ERROR IN fetchArticlesFromDB:');
    console.error('   Error name:', error?.name);
    console.error('   Error message:', error?.message);
    console.error('   Error stack:', error?.stack);
    console.log('═══════════════════════════════════════');
    return [];
  }
}

export async function fetchArticles(siteId, siteUrl) {
  console.log('');
  console.log('╔═══════════════════════════════════════╗');
  console.log('║   FETCH ARTICLES - COMBINED FETCH     ║');
  console.log('╚═══════════════════════════════════════╝');
  console.log('Parameters:');
  console.log('  siteId:', siteId);
  console.log('  siteUrl:', siteUrl);
  console.log('');
  
  try {
    const [wpArticles, dbArticles] = await Promise.all([
      siteUrl ? fetchArticlesFromWP(siteUrl) : Promise.resolve([]),
      fetchArticlesFromDB(siteId)
    ]);

    console.log('');
    console.log('╔═══════════════════════════════════════╗');
    console.log('║         FETCH RESULTS SUMMARY          ║');
    console.log('╚═══════════════════════════════════════╝');
    console.log('WordPress articles:', wpArticles.length);
    console.log('Database articles:', dbArticles.length);

    const allArticles = [...wpArticles, ...dbArticles];
    const uniqueArticles = allArticles.reduce((acc, article) => {
      const existing = acc.find(a => a.title === article.title);
      if (!existing) {
        acc.push(article);
      }
      return acc;
    }, []);

    console.log('Total combined:', allArticles.length);
    console.log('After deduplication:', uniqueArticles.length);
    
    if (uniqueArticles.length > 0) {
      console.log('');
      console.log('Sample of final articles (first 2):');
      uniqueArticles.slice(0, 2).forEach((article, idx) => {
        console.log(`  ${idx + 1}.`, article.title, `(${article.source})`);
      });
    } else {
      console.log('');
      console.log('⚠️ NO ARTICLES TO RETURN');
    }
    
    console.log('');
    console.log('✅ FETCH ARTICLES COMPLETE');
    console.log('═══════════════════════════════════════');
    return uniqueArticles;
  } catch (error) {
    console.error('');
    console.error('💥 UNEXPECTED ERROR IN fetchArticles:');
    console.error('   Error:', error);
    console.log('═══════════════════════════════════════');
    return [];
  }
}
